import React, { createContext, useContext, useMemo, useState } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import createTheme from '@mui/material/styles/createTheme';

type ColorMode = 'light' | 'dark';

type ColorModeContextValue = {
	mode: ColorMode;
	toggle: () => void;
};

const ColorModeContext = createContext<ColorModeContextValue | undefined>(undefined);

export const useColorMode = (): ColorModeContextValue => {
	const ctx = useContext(ColorModeContext);
	if (!ctx) throw new Error('useColorMode must be used within AppThemeProvider');
	return ctx;
};

export const AppThemeProvider = ({ children }: React.PropsWithChildren) => {
	const [mode, setMode] = useState<ColorMode>('light');

	const toggle = () => setMode((m) => (m === 'light' ? 'dark' : 'light'));

	const theme = useMemo(
		() =>
			createTheme({
				palette: {
					mode,
					primary: { main: mode === 'light' ? '#FF9800' : '#FFB74D' }, // Orange for beer theme
					secondary: { main: mode === 'light' ? '#FFC107' : '#FFD54F' }, // Amber/yellow
					background: {
						default: mode === 'light' ? '#FFFFFF' : '#0B0E1A',
						paper: mode === 'light' ? '#FFFFFF' : '#121624'
					},
					text: {
						primary: mode === 'light' ? '#111827' : '#E9ECF2',
						secondary: mode === 'light' ? '#4B5563' : '#A7AEBC'
					}
				},
				typography: {
					fontFamily: "'Inter', ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
					h1: { fontWeight: 800 },
					h2: { fontWeight: 800 },
					h3: { fontWeight: 800 },
					h4: { fontWeight: 800 }
				},
				shape: { borderRadius: 14 },
				components: {
					MuiPaper: {
						styleOverrides: {
							root: {
								backgroundImage: 'none'
							}
						}
					},
					MuiCard: {
						styleOverrides: {
							root: {
								border: mode === 'light'
									? '1px solid rgba(17,24,39,0.08)'
									: '1px solid rgba(255,255,255,0.06)'
							}
						}
					},
					MuiAppBar: {
						styleOverrides: {
							root: {
								backdropFilter: 'saturate(180%) blur(12px)',
								backgroundColor: mode === 'light'
									? 'rgba(255, 255, 255, 0.72)'
									: 'rgba(22, 26, 43, 0.6)'
							}
						}
					}
				}
			}),
		[mode]
	);

	return (
		<ColorModeContext.Provider value={{ mode, toggle }}>
			<MuiThemeProvider theme={theme}>
				<CssBaseline />
				{children}
			</MuiThemeProvider>
		</ColorModeContext.Provider>
	);
};
